/**
 * PublishedProjectPage - Read-only viewer for published project snapshots (RFC-0295).
 *
 * Route: /published/:shareToken
 * Fetches the published snapshot (public, no auth required) and renders a
 * read-only ReactFlow canvas using the same canvas-v2 node types as the
 * workspace. No Yjs, no WebSocket, no chat sidebar.
 *
 * Admin users see a version picker to browse historical snapshots
 * and the live workspace state.
 */

import { useState, useMemo, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocaleFormat } from '../i18n/useLocaleFormat'
import { useParams } from 'react-router-dom'
import ReactFlow, { ReactFlowProvider, Background, BackgroundVariant, MiniMap, PanOnScrollMode, useNodesState, useEdgesState } from 'reactflow'
import * as Y from 'yjs'
import 'reactflow/dist/style.css'
import {
  nodeTypes,
  edgeTypes,
  CanvasContext,
  CanvasEdgesProvider,
  type CanvasContextValue,
  NodeHoverContext,
  type NodeHoverContextValue,
} from '../components/canvas-v2'
import { GroupDropTargetProvider } from '../components/canvas-v2/nodes/GroupNode/GroupDropTargetContext'
import Markdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { Copy, Loader2 } from 'lucide-react'
import { usePublishedSnapshot, usePublicationVersions, usePublicationVersion, usePublicationLive } from '../api/hooks'
import { useCloneTemplate } from '../api/useCloneTemplate'
import { setPublicationToken, type PublishedChatMessage, type PublishedNodeData, type PublishedSnapshot } from '../api/client'
import { useAuth } from '../contexts/AuthContext'
import { ReadOnlyContext } from '../contexts/ReadOnlyContext'
import { ProjectIdProvider } from '../contexts/ProjectIdContext'
import { APP_NAME } from '../constants/brand'
import { preparePublishedSnapshotNodes } from './publishedSnapshotNodes'
import { readNodeDataFromYMap, type RawCanvasYNode, type ReactFlowCanvasEdge, type ReactFlowCanvasNode } from '../hooks/useYCanvas'
import { deriveGroupedNodeLayout } from '../components/canvas-v2/nodes/GroupNode/GroupNode'
import { SingleNodeDetailsProvider } from '../components/canvas-v2/nodes/shared/SingleNodeDetailsContext'
import { createNadDebug } from '../utils/debugLog'
import { buildCandidates } from '../components/canvas-v2/nodes/shared/mentionUtils'
import {
  MENTION_MARKER_RE,
  MentionMarkupText,
  MentionRenderProvider,
  ResolvedMentionChip,
  remarkMentionChips,
} from '../components/chat/MentionMarkup'
import { trackInteraction } from '../analytics/interactionAnalytics'

const publishedChatLog = createNadDebug('dw:published-chat-mentions:resolution')
const CHAT_REMARK_PLUGINS = [remarkGfm, remarkMentionChips]

const publishedVideoEditorHydrationLog = createNadDebug(
  'dw:published-video-editor-hydration:page',
)

type SnapshotYMapValue = RawCanvasYNode extends Y.Map<infer TValue> ? TValue : never
type PublishedDisplayData = {
  projectName: string
  snapshot: PublishedSnapshot['snapshot']
  messages: PublishedChatMessage[]
}

// ---------------------------------------------------------------------------
// ReadOnlyChatPanel — displays snapshotted conversation messages
// ---------------------------------------------------------------------------

/** Strip internal code blocks (dag-proposal, story-structure, etc.) */
function stripInternalBlocks(content: string): string {
  return content
    .replace(/```dag-proposal[\s\S]*?```/g, '')
    .replace(/```story-structure[\s\S]*?```/g, '')
    .replace(/```story-direction[\s\S]*?```/g, '')
    .trim()
}

/** Clean AI-generated text by replacing em-dashes */
function cleanAiText(text: string): string {
  return text
    .replace(/\s*—\s*/g, ', ')
    .replace(/\s*–\s*/g, ', ')
}

/** Markdown component overrides matching AgentChat styling */
const markdownComponents = {
  span: ({ children, ...props }: { children?: React.ReactNode; 'data-mention-node-id'?: string }) => {
    const nodeId = props['data-mention-node-id']
    if (nodeId) return <ResolvedMentionChip nodeId={nodeId} surface="published_assistant" interactive={false} />
    return <span>{children}</span>
  },
  ul: ({ children }: { children?: React.ReactNode }) => (
    <ul className="list-disc pl-4 my-1.5 space-y-0.5">{children}</ul>
  ),
  ol: ({ children }: { children?: React.ReactNode }) => (
    <ol className="list-decimal pl-4 my-1.5 space-y-0.5">{children}</ol>
  ),
  li: ({ children }: { children?: React.ReactNode }) => (
    <li className="text-text-secondary">{children}</li>
  ),
  p: ({ children }: { children?: React.ReactNode }) => (
    <p className="my-1.5">{children}</p>
  ),
  strong: ({ children }: { children?: React.ReactNode }) => (
    <strong className="font-medium text-text-primary">{children}</strong>
  ),
  em: ({ children }: { children?: React.ReactNode }) => (
    <em className="text-text-secondary">{children}</em>
  ),
  code: ({ children, className }: { children?: React.ReactNode; className?: string }) => {
    const isInline = !className?.includes('language-')
    if (isInline) {
      return (
        <code className="bg-surface-1 px-1 py-0.5 rounded text-xs font-medium font-mono text-text-primary">{children}</code>
      )
    }
    return <>{children}</>
  },
  pre: ({ children }: { children?: React.ReactNode }) => (
    <pre className="bg-surface-1 border border-border rounded-lg p-3 my-2 overflow-x-auto text-xs font-medium">{children}</pre>
  ),
  h1: ({ children }: { children?: React.ReactNode }) => (
    <h1 className="text-base font-medium text-text-primary mt-2 mb-1">{children}</h1>
  ),
  h2: ({ children }: { children?: React.ReactNode }) => (
    <h2 className="text-sm font-medium text-text-primary mt-2 mb-1">{children}</h2>
  ),
  h3: ({ children }: { children?: React.ReactNode }) => (
    <h3 className="text-sm font-medium text-text-primary mt-1.5 mb-0.5">{children}</h3>
  ),
  a: ({ children }: { href?: string; children?: React.ReactNode }) => (
    <span className="text-text-secondary underline underline-offset-2">{children}</span>
  ),
  blockquote: ({ children }: { children?: React.ReactNode }) => (
    <blockquote className="border-l border-border pl-2 my-1.5 text-text-muted">{children}</blockquote>
  ),
  table: ({ children }: { children?: React.ReactNode }) => (
    <div className="my-2 overflow-x-auto">
      <table className="min-w-full text-xs font-medium">{children}</table>
    </div>
  ),
  thead: ({ children }: { children?: React.ReactNode }) => (
    <thead className="bg-panel">{children}</thead>
  ),
  tbody: ({ children }: { children?: React.ReactNode }) => (
    <tbody className="divide-y divide-border-subtle">{children}</tbody>
  ),
  tr: ({ children }: { children?: React.ReactNode }) => (
    <tr className="border-b border-border-subtle">{children}</tr>
  ),
  th: ({ children }: { children?: React.ReactNode }) => (
    <th className="px-3 py-2 text-left font-semibold text-text-secondary border border-border">{children}</th>
  ),
  td: ({ children }: { children?: React.ReactNode }) => (
    <td className="px-3 py-2 font-normal text-text-secondary border border-border-subtle">{children}</td>
  ),
}

function ReadOnlyChatPanel({ messages, snapshotNodes, projectId }: {
  messages: PublishedChatMessage[]
  snapshotNodes: ReactFlowCanvasNode[]
  projectId?: string
}) {
  const { t } = useTranslation('pages')
  const [collapsed, setCollapsed] = useState(false)
  const { t: chatT } = useTranslation('workspace')
  const mentionValue = useMemo(() => ({
    candidates: buildCandidates(snapshotNodes),
    projectId,
    focusLabel: '',
    removedLabel: chatT('agentChat.removedNodeChip'),
  }), [snapshotNodes, projectId, chatT])

  // Filter messages the same way AgentChat does:
  // 1. Truncate at the first message with unanswered questions (hide everything after it)
  // 2. Hide assistant messages whose only questions are answered node/cost confirmations (no visible content)
  // 3. Hide user "continue"/"confirm" replies that follow such messages
  // 4. Hide empty assistant messages with no visible content (unless it's the one with unanswered questions)
  const { displayMessages, currentQuestions } = useMemo(() => {
    // Find the first message with unanswered questions
    let firstUnansweredIdx = -1
    for (let i = 0; i < messages.length; i++) {
      if (messages[i].questions?.some((q) => q.answer == null)) {
        firstUnansweredIdx = i
        break
      }
    }

    // Truncate: only show messages up to and including the first unanswered
    const slice = firstUnansweredIdx === -1
      ? messages
      : messages.slice(0, firstUnansweredIdx + 1)

    const isAutoAcceptedAssistant = (msg: PublishedChatMessage) => {
      if (msg.role !== 'assistant' || !msg.questions?.length) return false
      return msg.questions.every((q) => {
        if (q.answer == null) return false
        const toolUseId = q.tool_use_id
        if (toolUseId?.startsWith('__cost_confirmation__')) return true
        return false
      })
    }

    const filtered = slice.filter((msg, i, arr) => {
      // Hide empty assistant messages unless they carry unanswered questions
      const hasUnansweredQuestions = msg.questions?.some((q) => q.answer == null)
      if (msg.role === 'assistant' && !stripInternalBlocks(msg.content || '').trim() && !hasUnansweredQuestions) return false
      if (isAutoAcceptedAssistant(msg) && !msg.content?.trim()) return false
      if (msg.role === 'user' && (msg.content === 'continue' || msg.content === 'confirm')) {
        const prev = arr[i - 1]
        if (prev && isAutoAcceptedAssistant(prev) && !prev.content?.trim()) return false
      }
      return true
    })

    // Extract unanswered questions from the first unanswered message only
    const currentQuestions = firstUnansweredIdx === -1
      ? []
      : (messages[firstUnansweredIdx].questions || []).filter(
          (q) => q.answer == null
        )

    return { displayMessages: filtered, currentQuestions }
  }, [messages])

  useEffect(() => {
    const candidateIds = new Set(mentionValue.candidates.map((candidate) => candidate.id))
    const references = displayMessages.flatMap((message) => (
      [...message.content.matchAll(MENTION_MARKER_RE)].map((match) => match[1])
    ))
    publishedChatLog({
      event: 'published-chat:reference-resolution-summary',
      project_id: projectId,
      messages_scanned_count: displayMessages.length,
      snapshot_node_count: candidateIds.size,
      marker_occurrence_count: references.length,
      resolved_marker_count: references.filter((id) => candidateIds.has(id)).length,
      missing_target_marker_count: references.filter((id) => !candidateIds.has(id)).length,
      resolved_unique_target_count: new Set(references.filter((id) => candidateIds.has(id))).size,
      missing_unique_target_count: new Set(references.filter((id) => !candidateIds.has(id))).size,
      missing_target_display: 'removed_node_chip',
      view_mode: 'published_read_only',
    })
  }, [displayMessages, mentionValue, projectId])

  if (displayMessages.length === 0 && currentQuestions.length === 0) return null

  const panel = (
    <div
      className={`shrink-0 border-r border-border bg-card flex flex-col transition-all duration-200 ${
        collapsed ? 'w-10' : 'w-[360px]'
      }`}
      data-testid="published-chat-panel"
    >
      {/* Collapse toggle */}
      <div className="h-12 border-b border-border flex items-center px-3 shrink-0">
        <button
          onClick={() => setCollapsed(!collapsed)}
          className="p-1 rounded hover:bg-white/10 text-white/60 hover:text-white transition-colors"
          title={collapsed ? t('publishedProject.expandChat') : t('publishedProject.collapseChat')}
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            {collapsed ? (
              <path strokeLinecap="round" strokeLinejoin="round" d="M13 5l7 7-7 7M5 5l7 7-7 7" />
            ) : (
              <path strokeLinecap="round" strokeLinejoin="round" d="M11 19l-7-7 7-7M19 19l-7-7 7-7" />
            )}
          </svg>
        </button>
        {!collapsed && (
          <span className="ml-2 text-sm font-medium text-white/80">{t('publishedProject.conversation')}</span>
        )}
      </div>

      {/* Messages */}
      {!collapsed && (
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {displayMessages.map((msg, i) => {
            const isUser = msg.role === 'user'
            const cleanContent = cleanAiText(stripInternalBlocks(msg.content))

            if (isUser) {
              return (
                <div key={i} className="flex justify-end">
                  <div className="max-w-[85%] px-3 py-2 text-base leading-relaxed text-text-primary bg-white/10 border border-white/10 rounded-lg rounded-br-sm pointer-events-none select-text">
                    <div className="break-words whitespace-pre-wrap">
                      <MentionMarkupText text={cleanContent} surface="published_user" interactive={false} />
                    </div>
                  </div>
                </div>
              )
            }

            return (
              <div key={i} className="space-y-1.5 pointer-events-none select-text">
                <div className="flex items-center gap-1.5">
                  <svg className="w-3 h-3 text-primary" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" />
                  </svg>
                  <span className="text-sm font-medium text-text-muted">{t('publishedProject.director')}</span>
                </div>
                {cleanContent && (
                  <div className="text-base leading-[1.6] text-text-primary break-words">
                    <Markdown remarkPlugins={CHAT_REMARK_PLUGINS} components={markdownComponents}>
                      {cleanContent}
                    </Markdown>
                  </div>
                )}
              </div>
            )
          })}

          {/* Current questions — rendered once at the bottom, matching AgentChat */}
          {currentQuestions.map((q, qi) => {
            const options = q.options ?? []
            const question = q.question
            const context = q.context

            // Single option: inline text style (matches QuestionCard)
            if (options.length === 1) {
              const opt = options[0]
              const optionLabel = opt.label || opt.value
              return (
                <div key={qi} className="my-3 pointer-events-none select-text">
                  {context && (
                    <div data-testid="published-question-context" className="mb-1.5 text-base text-muted-foreground">
                      <MentionMarkupText text={context} surface="published_assistant" interactive={false} />
                    </div>
                  )}
                  <div className="text-base text-text-secondary">
                    {question}{' '}
                    <span className="text-text-primary underline underline-offset-2">{optionLabel}</span>
                  </div>
                </div>
              )
            }

            // Multiple options: card style
            return (
              <div key={qi} className="my-4 pointer-events-none select-text">
                {context && (
                  <div data-testid="published-question-context" className="mb-1.5 text-base text-muted-foreground">
                    <MentionMarkupText text={context} surface="published_assistant" interactive={false} />
                  </div>
                )}
                {question && (
                  <div className="mb-3">
                    <span className="text-base text-text-secondary">{question}</span>
                  </div>
                )}
                <div className="flex flex-col gap-1.5">
                  {options.map((opt, oi) => {
                    const optionLabel = opt.label || opt.value
                    const description = opt.description
                    return (
                      <div
                        key={oi}
                        className="w-full px-3 py-2 text-left rounded border border-border-subtle opacity-60"
                      >
                        <span className="text-sm font-medium text-text-secondary">{optionLabel}</span>
                        {description && (
                          <span className="block text-xs font-medium text-text-muted mt-0.5">{description}</span>
                        )}
                      </div>
                    )
                  })}
                </div>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
  return <MentionRenderProvider value={mentionValue}>{panel}</MentionRenderProvider>
}

// ---------------------------------------------------------------------------
// Deserialize snapshot node data through generated YDoc field readers
// ---------------------------------------------------------------------------

function deserializeSnapshotData(
  feType: string,
  raw: PublishedNodeData,
  projectId: string | undefined,
): ReactFlowCanvasNode['data'] {
  const doc = new Y.Doc()
  const ynode: RawCanvasYNode = doc.getMap('published-node')
  ynode.set('type', feType)
  for (const [key, value] of Object.entries(raw)) {
    ynode.set(key, snapshotValueToYDoc(value))
  }
  return {
    ...readNodeDataFromYMap(ynode),
    projectId,
  }
}

function snapshotValueToYDoc(value: PublishedNodeData[string]): SnapshotYMapValue {
  if (value === null || value === undefined) return value
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') return value
  if (Array.isArray(value)) {
    const yarr = new Y.Array<SnapshotYMapValue>()
    yarr.push(value.map((item) => snapshotValueToYDoc(item)))
    return yarr
  }
  if (typeof value === 'object') {
    const ymap = new Y.Map<SnapshotYMapValue>()
    for (const [key, item] of Object.entries(value)) {
      ymap.set(key, snapshotValueToYDoc(item))
    }
    return ymap
  }
  return undefined
}

// ---------------------------------------------------------------------------
// No-op CanvasContext value for read-only mode
// ---------------------------------------------------------------------------

const READ_ONLY_CANVAS_CONTEXT: CanvasContextValue = {
  addNode: () => '',
  addEdge: () => {},
  removeNode: () => {},
  removeEdge: () => {},
  duplicateNode: () => undefined,
  updateNodeData: () => {},
  persistNodeData: () => {},
  updateNodeParent: () => {},
  executeNode: () => {},
  getNode: () => undefined,
  getNodes: () => [],
  updateNodePosition: () => {},
  replacePortEdges: () => {},
}

const READ_ONLY_HOVER_CONTEXT: NodeHoverContextValue = {
  hoveredNodeId: null,
  setHoveredNodeId: () => {},
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

function PublishedProjectPage() {
  const { t } = useTranslation('pages')
  const { formatDate } = useLocaleFormat()
  const { projectToken, versionSlug } = useParams<{ projectToken: string; versionSlug: string }>()

  const { user } = useAuth()
  const isAdmin = user?.user_metadata?.role === 'admin'

  // Set publication token for unauthenticated asset URL fallback
  useEffect(() => {
    if (projectToken) {
      setPublicationToken(projectToken)
    }
    return () => setPublicationToken(null)
  }, [projectToken])

  // Version picker state (admin only)
  const [selectedVersion, setSelectedVersion] = useState<'latest' | 'live' | number>('latest')

  // Clone flow — shared with the templates gallery (RFC-0934). The hook owns the
  // P0-6 in-place navigation and the P0-7 specific-error mapping; this button
  // only renders for logged-in users (see below), so the hook's logged-out
  // /login redirect never fires here.
  const { cloneTemplate, isCloning, cloneErrorMessage } = useCloneTemplate()

  // Fetch the public snapshot
  const {
    data: publishedData,
    isLoading: snapshotLoading,
    error: snapshotError,
  } = usePublishedSnapshot(projectToken, versionSlug)

  // Admin: fetch version list (only when admin and we have projectId from snapshot)
  const adminProjectId = isAdmin && publishedData ? publishedData.project_id : undefined
  const { data: versions } = usePublicationVersions(adminProjectId)

  // Admin: fetch specific version (when selecting a historical version)
  const { data: versionData } = usePublicationVersion(
    adminProjectId,
    typeof selectedVersion === 'number' ? selectedVersion : undefined
  )

  // Admin: fetch live snapshot
  const { data: liveData } = usePublicationLive(
    selectedVersion === 'live' ? adminProjectId : undefined
  )

  // Determine which snapshot to display
  const displayData = useMemo<PublishedDisplayData | null>(() => {
    if (selectedVersion === 'live' && liveData) {
      return {
        projectName: liveData.project_name,
        snapshot: liveData.snapshot,
        messages: [],
      }
    }
    if (typeof selectedVersion === 'number' && versionData) {
      return {
        projectName: versionData.project_name,
        snapshot: versionData.snapshot,
        messages: [],
      }
    }
    if (publishedData) {
      return {
        projectName: publishedData.project_name,
        snapshot: publishedData.snapshot,
        messages: publishedData.messages || [],
      }
    }
    return null
  }, [selectedVersion, publishedData, versionData, liveData])

  // Published mode is always read-only for all viewers
  const isReadOnly = true

  // Convert snapshot to ReactFlow nodes and edges
  const projectId = publishedData?.project_id

  const { initialNodes, initialEdges } = useMemo(() => {
    if (!displayData?.snapshot) return { initialNodes: [], initialEdges: [] }

    const snap = displayData.snapshot
    // Build nodes, filtering out hidden types (same as useYCanvas), then sort
    // so parents appear before children (ReactFlow requirement)
    const { nodes: renderableSnapshotNodes, placements } =
      preparePublishedSnapshotNodes(snap.nodes || [])
    const rfNodes: ReactFlowCanvasNode[] = renderableSnapshotNodes.map((n) => {
      const placement = placements.get(n.id)
      if (!placement) throw new Error(`Missing published snapshot placement for ${n.id}`)
      const node: ReactFlowCanvasNode = {
        id: n.id,
        type: n.type,
        position: placement.position,
        data: deserializeSnapshotData(n.type, n.data || {}, projectId),
      }
      // Filtered parent offsets are folded into position by the planner.
      if (placement.parentId) node.parentId = placement.parentId
      if (placement.extent) node.extent = placement.extent
      // Preserve explicit dimensions (group nodes)
      if (n.width != null) node.width = n.width
      if (n.height != null) node.height = n.height
      return node
    })
    // Group children (storyboard panels, webtoon pages, short-video clips) get
    // their positions and container size derived from in_child_node_ids — the
    // editor runs this on every render (useYCanvas.ts) and never persists the
    // resulting positions to YDoc, so the snapshot stores stale (0,0) for
    // every grouped child. Run the same derivation here, otherwise panels
    // stack at the group's origin.
    // deriveGroupedNodeLayout now enforces parent-before-child array order
    // (ReactFlow requires parents before children) via its internal
    // sortParentsBeforeChildren pass — single source of truth, no separate
    // sort needed here.
    const derivedNodes = deriveGroupedNodeLayout(rfNodes)

    const rfEdges: ReactFlowCanvasEdge[] = (snap.edges || []).map((e) => ({
      id: e.id,
      source: e.source,
      target: e.target,
      type: e.type || 'default',
      sourceHandle: e.from_port,
      targetHandle: e.to_port,
      data: {
        fromIndex: e.fromIndex,
        toIndex: e.toIndex,
      },
    }))

    return {
      initialNodes: derivedNodes,
      initialEdges: rfEdges,
    }
  }, [displayData, projectId])

  // Mutable state so ReactFlow can update positions on drag
  const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes)
  const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdges)
  const selectedNodeCount = useMemo(
    () => nodes.reduce((count, node) => count + (node.selected ? 1 : 0), 0),
    [nodes],
  )

  // Re-seed when the async snapshot changes (cold load, version picker, etc.).
  // ReactFlow state is initialized before the public query resolves, so both
  // collections must move together or node components see a split-brain graph.
  useEffect(() => {
    if (!displayData?.snapshot) return

    const videoEditorIds = new Set(
      initialNodes.filter((node) => node.type === 'videoEditor').map((node) => node.id),
    )
    const videoInputEdgeCount = initialEdges.filter(
      (edge) =>
        videoEditorIds.has(edge.target) &&
        (edge.targetHandle === 'in_videos' || edge.targetHandle === 'in_video'),
    ).length

    setNodes(initialNodes)
    setEdges(initialEdges)
    const logTimer = window.setTimeout(() => {
      publishedVideoEditorHydrationLog({
        event: 'published-snapshot-seed-start',
        trace_id: `published-video-editor-hydration:${projectId ?? 'unknown'}`,
        projectId,
        nodeCount: initialNodes.length,
        edgeCount: initialEdges.length,
        videoEditorCount: videoEditorIds.size,
      })
      publishedVideoEditorHydrationLog({
        event: 'published-canvas-edges-seeded',
        trace_id: `published-video-editor-hydration:${projectId ?? 'unknown'}`,
        projectId,
        edgeCount: initialEdges.length,
        videoInputEdgeCount,
      })
    }, 0)
    return () => window.clearTimeout(logTimer)
  }, [displayData, initialEdges, initialNodes, projectId, setEdges, setNodes])

  // Loading state
  if (snapshotLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-surface">
        <div className="flex flex-col items-center gap-4">
          <svg className="animate-spin h-8 w-8 text-primary" viewBox="0 0 24 24">
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
              fill="none"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            />
          </svg>
          <span className="text-white/60">{t('publishedProject.loading')}</span>
        </div>
      </div>
    )
  }

  // Error state
  if (snapshotError || !publishedData) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-surface">
        <div className="text-center max-w-md">
          <h1 className="text-2xl font-bold text-white mb-2">{t('publishedProject.notFound')}</h1>
          <p className="text-white/60">
            {snapshotError instanceof Error
              ? t('publishedProject.invalidLink')
              : t('publishedProject.couldNotLoad')}
          </p>
        </div>
      </div>
    )
  }

  return (
    <ProjectIdProvider value={projectId}>
    <ReadOnlyContext.Provider value={isReadOnly}>
      <CanvasContext.Provider value={READ_ONLY_CANVAS_CONTEXT}>
        <NodeHoverContext.Provider value={READ_ONLY_HOVER_CONTEXT}>
          <div className="h-[calc(100vh-var(--banner-height,0px))] w-screen flex flex-col bg-surface" data-testid="published-project-page">
            {/* Header */}
            <header className="h-12 border-b border-border flex items-center px-4 bg-card shrink-0">
              <div className="flex items-center gap-3 flex-1">
                <span className="text-sm font-semibold text-primary">{APP_NAME}</span>
                <span className="text-white/30">|</span>
                <h1 className="text-sm font-normal text-white truncate" data-testid="published-project-name">
                  {displayData?.projectName || t('publishedProject.fallbackName')}
                </h1>
              </div>

              {/* Admin version picker */}
              {isAdmin && versions && versions.length > 0 && (
                <select
                  className="text-xs font-medium bg-surface border border-border rounded px-2 py-1 text-white"
                  value={selectedVersion.toString()}
                  onChange={(e) => {
                    const val = e.target.value
                    if (val === 'latest') setSelectedVersion('latest')
                    else if (val === 'live') setSelectedVersion('live')
                    else setSelectedVersion(parseInt(val, 10))
                  }}
                  data-testid="version-picker"
                >
                  <option value="latest">{t('publishedProject.latest')}</option>
                  <option value="live">{t('publishedProject.live')}</option>
                  {versions.map((v) => (
                    <option key={v.version_number} value={v.version_number.toString()}>
                      v{v.version_number} - {formatDate(v.created_at)}
                    </option>
                  ))}
                </select>
              )}

              {/* Clone button — visible only for logged-in users */}
              {user && (
                <button
                  onClick={() => {
                    trackInteraction('published_project_clone_clicked', {})
                    cloneTemplate(projectToken, versionSlug)
                  }}
                  disabled={isCloning}
                  className="ml-3 flex items-center gap-1.5 text-xs font-medium bg-primary/90 hover:bg-primary text-white px-3 py-1.5 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="clone-project-button"
                >
                  {isCloning ? (
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  ) : (
                    <Copy className="w-3.5 h-3.5" />
                  )}
                  {isCloning ? t('publishedProject.cloning') : t('publishedProject.cloneProject')}
                </button>
              )}

              {cloneErrorMessage && (
                <span
                  role="alert"
                  className="ml-2 text-xs font-medium text-red-400"
                  data-testid="clone-error-message"
                >
                  {cloneErrorMessage}
                </span>
              )}

              <span className="ml-3 text-xs font-medium text-white/40 bg-white/5 px-2 py-1 rounded" data-testid="read-only-badge">
                {t('publishedProject.readOnly')}
              </span>
            </header>

            {/* Content: Chat Panel + Canvas */}
            <div className="flex-1 flex overflow-hidden">
              {/* Chat Panel (left) */}
              <ReadOnlyChatPanel
                messages={displayData?.messages || []}
                snapshotNodes={initialNodes}
                projectId={projectId}
              />

              {/* Canvas (right) */}
              <div className="flex-1">
                <CanvasEdgesProvider edges={edges} hideConnectionHandles>
                  <SingleNodeDetailsProvider selectedNodeCount={selectedNodeCount}>
                  <ReactFlow
                    nodes={nodes}
                    edges={edges}
                    onNodesChange={onNodesChange}
                    onEdgesChange={onEdgesChange}
                    nodeTypes={nodeTypes}
                    edgeTypes={edgeTypes}
                    nodesDraggable={true}
                    nodesConnectable={false}
                    elementsSelectable={true}
                    panOnDrag={true}
                    panOnScroll
                    panOnScrollMode={PanOnScrollMode.Free}
                    zoomOnScroll={false}
                    zoomOnPinch
                    minZoom={0.1}
                    maxZoom={4}
                    defaultViewport={{ x: 0, y: 0, zoom: 0.5 }}
                    fitView
                    className="bg-background canvas-gradient-bg"
                    proOptions={{ hideAttribution: true }}
                  >
                    <Background variant={BackgroundVariant.Dots} gap={24} size={1} color="rgba(255,255,255,0.08)" />
                    <MiniMap
                      className="!bg-card !border !border-border !rounded-lg !shadow-sm overflow-hidden"
                      zoomable
                      pannable
                      nodeColor={(node) => {
                        const status = typeof node.data?.status === 'string' ? node.data.status : undefined
                        if (status === 'running') return '#22c55e'
                        if (status === 'error') return '#ef4444'
                        if (status === 'success') return '#3b82f6'
                        return '#6b7280'
                      }}
                      maskColor="rgba(0, 0, 0, 0.5)"
                      style={{ width: 160, height: 100 }}
                    />
                  </ReactFlow>
                  </SingleNodeDetailsProvider>
                </CanvasEdgesProvider>
              </div>
            </div>
          </div>
        </NodeHoverContext.Provider>
      </CanvasContext.Provider>
    </ReadOnlyContext.Provider>
    </ProjectIdProvider>
  )
}

// ---------------------------------------------------------------------------
// Wrapped export with ReactFlowProvider + GroupDropTargetProvider
// ---------------------------------------------------------------------------

export default function PublishedProjectPageWrapper() {
  return (
    <ReactFlowProvider>
      <GroupDropTargetProvider>
        <PublishedProjectPage />
      </GroupDropTargetProvider>
    </ReactFlowProvider>
  )
}
